import { forwardRef, useMemo } from 'react'

import { themeIconColorMap, themeTextColorMap } from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'

import { getTestProps } from '../../utils/testing.ts'
import { IconComponentFactory } from './factory.tsx'
import { isCoreIconName } from './remix/core.ts'
import { isOutlineSvgIconName } from './svg/types/names.ts'
import { iconSizeClassMap, iconStrokeWidthClassMap } from './types/maps.ts'
import type { IconProps } from './types/props.ts'

const Icon = forwardRef<SVGSVGElement, IconProps>(
  (
    {
      className,
      color,
      dataTestId,
      dataTrackingId,
      disabled,
      fill,
      name,
      size,
      strokeWidth,
      ...props
    },
    ref
  ) => {
    const IconComponent = useMemo(
      () => IconComponentFactory({ name, fill, size, color }),
      [name, fill, size, color]
    )

    const colorName = disabled ? 'disabled' : (color ?? 'primary')
    const colorClasses =
      isOutlineSvgIconName(name) || isCoreIconName(name)
        ? themeTextColorMap[colorName]
        : themeIconColorMap[colorName]

    return (
      <IconComponent
        {...props}
        className={cn(
          'qf-icon shrink-0',
          size !== 'inherit' && iconSizeClassMap[size],
          strokeWidth !== undefined && iconStrokeWidthClassMap[strokeWidth],
          colorClasses,
          className
        )}
        {...getTestProps({ dataTestId, dataTrackingId })}
        ref={ref}
      />
    )
  }
)

export * from './remix/core.ts'
export * from './remix/remix.ts'
export * from './remix/types.ts'
export * from './svg/types/names.ts'
export * from './types/core.ts'
export * from './types/maps.ts'
export * from './types/names.ts'
export * from './types/props.ts'
export { Icon, type IconProps }
